// Form Component to Add or Edit Config Group

import { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TConfigSpare } from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  AddEditSpare,
  GetSpareProductList,
  GetSpecificSpare,
} from "../../../utils/api.constant";
import TextArea from "antd/es/input/TextArea";
import CustomDropdown from "../../../components/custom-dropdown/custom-dropdown";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type TDrawerProps = {
  groupUniqueId: string;
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * Form Component to Add or Edit Config Group
 *
 * @param {DrawerProps} props - drawer props
 * @returns {JSX.Element}
 */
export default function ConfigSpareForm(props: TDrawerProps) {
  const [form] = Form.useForm(); // Form instance
  const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state
  const { post } = useFetch(); // Hook to make API calls
  const { onShowToast } = useToast(); // Hook to show toast
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  // Hook to handle on load operations
  useEffect(() => {
    fetchSpecificSpare(); // Fetch specific group
  }, []);

  // default values initial state
  const defaultValues: TConfigSpare = {
    id: 0,
    productName: "",
    partDescription: "",
    partNumber: "",
  };

  // Handle on submit event
  const onFinish = async (values: TConfigSpare) => {
    try {
      const payload = {
        data: {
          ...values,
          id: props.id ? props.id : 0,
          productName: selectedProduct,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditSpare, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpecificSpare = async () => {
    if (props.id) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificSpare, payload);
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const { data } = response;
          setSelectedProduct(data?.productName);
          form.setFieldsValue(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="productId"
                label="Product Name"
                rules={[
                  {
                    required: true,
                    message: "Please enter product name",
                  },
                ]}
              >
                <CustomDropdown
                  endPoint={GetSpareProductList}
                  value={form.getFieldValue("productId")}
                  selectedName={selectedProduct}
                  onChange={(selected: any) => {
                    form.setFieldValue("productName", selected.label);
                    form.setFieldValue("productId", selected.value);
                    setSelectedProduct(selected.label);
                  }}
                  label=""
                  placeHolder="Select product"
                  optionName="productName"
                  optionId="id"
                ></CustomDropdown>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="partNumber"
                label="Part Number"
                rules={[
                  {
                    required: true,
                    message: "Please enter part number",
                  },
                ]}
              >
                <Input autoComplete="off" placeholder="Enter part number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item name="partDescription" label="Description">
                <TextArea
                  rows={4}
                  placeholder="Enter the description..."
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              className="btnDimensions"
              onClick={form.submit}
              disabled={isLoading}
            >
              Save
            </Button>
            <Button
              type="primary"
              disabled={isLoading}
              className="btnDimensions"
              onClick={props.onCloseDrawer}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
